import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from '@/lib/auth';
import { AppShell } from '@/components/shell/AppShell';

import PublicLayout from '@/pages/public/PublicLayout';
import HomePage from '@/pages/public/HomePage';
import FeaturesPage from '@/pages/public/FeaturesPage';
import HydropowerUseCasePage from '@/pages/public/HydropowerUseCasePage';
import RequestDemoPage from '@/pages/public/RequestDemoPage';
import ContactPage from '@/pages/public/ContactPage';
import PrivacyPage from '@/pages/public/PrivacyPage';
import TermsPage from '@/pages/public/TermsPage';
import LoginPage from '@/pages/auth/LoginPage';

import DashboardPage from '@/pages/app/DashboardPage';
import AssetsPage from '@/pages/app/AssetsPage';
import AssetDetailPage from '@/pages/app/AssetDetailPage';
import AssetEditPage from '@/pages/app/AssetEditPage';
import ObservationsPage from '@/pages/app/ObservationsPage';
import ObservationDetailPage from '@/pages/app/ObservationDetailPage';
import DetectionsPage from '@/pages/app/DetectionsPage';
import DetectionDetailPage from '@/pages/app/DetectionDetailPage';
import AlertsPage from '@/pages/app/AlertsPage';
import AlertDetailPage from '@/pages/app/AlertDetailPage';
import IncidentsPage from '@/pages/app/IncidentsPage';
import IncidentDetailPage from '@/pages/app/IncidentDetailPage';
import FieldVerifyPage from '@/pages/app/FieldVerifyPage';
import ReportsPage from '@/pages/app/ReportsPage';
import AuditPage from '@/pages/app/AuditPage';
import UsersAdminPage from '@/pages/app/UsersAdminPage';
import OrganizationsAdminPage from '@/pages/app/OrganizationsAdminPage';
import AdminConfigPage from '@/pages/app/AdminConfigPage';
import SystemHealthPage from '@/pages/app/SystemHealthPage';
import SearchPage from '@/pages/app/SearchPage';
import PresentationModePage from '@/pages/app/PresentationModePage';

import '@/App.css';

function App() {
  useEffect(() => {
    // default to dark theme for command centre; light for public
    const saved = localStorage.getItem('ad_theme');
    if (saved === 'light') {
      document.documentElement.classList.remove('dark');
    } else if (saved === 'dark') {
      document.documentElement.classList.add('dark');
    }
  }, []);

  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* Public marketing site */}
          <Route element={<PublicLayout />}>
            <Route path="/" element={<HomePage />} />
            <Route path="/features" element={<FeaturesPage />} />
            <Route path="/use-cases/hydropower" element={<HydropowerUseCasePage />} />
            <Route path="/request-demo" element={<RequestDemoPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/terms" element={<TermsPage />} />
          </Route>

          <Route path="/login" element={<LoginPage />} />

          {/* Presentation mode - full-screen, requires auth but bypasses AppShell */}
          <Route path="/app/presentation" element={<PresentationModePage />} />

          {/* Authenticated app */}
          <Route path="/app" element={<AppShell />}>
            <Route index element={<Navigate to="/app/dashboard" replace />} />
            <Route path="dashboard" element={<DashboardPage />} />
            <Route path="assets" element={<AssetsPage />} />
            <Route path="assets/:assetId" element={<AssetDetailPage />} />
            <Route path="assets/:assetId/edit" element={<AssetEditPage />} />
            <Route path="observations" element={<ObservationsPage />} />
            <Route path="observations/:obsId" element={<ObservationDetailPage />} />
            <Route path="detections" element={<DetectionsPage />} />
            <Route path="detections/:detId" element={<DetectionDetailPage />} />
            <Route path="alerts" element={<AlertsPage />} />
            <Route path="alerts/:alertId" element={<AlertDetailPage />} />
            <Route path="incidents" element={<IncidentsPage />} />
            <Route path="incidents/:incidentId" element={<IncidentDetailPage />} />
            <Route path="incidents/:incidentId/verify" element={<FieldVerifyPage />} />
            <Route path="reports" element={<ReportsPage />} />
            <Route path="audit" element={<AuditPage />} />
            <Route path="admin/users" element={<UsersAdminPage />} />
            <Route path="admin/organizations" element={<OrganizationsAdminPage />} />
            <Route path="admin/config" element={<AdminConfigPage />} />
            <Route path="admin/health" element={<SystemHealthPage />} />
            <Route path="search" element={<SearchPage />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
